import { Validate } from 'class-validator';
import { computed, toJS } from 'mobx';
import * as uuid from 'uuid';
import * as moment from 'moment';

import { EntityModel } from 'src/core/services/data/models';
import { DataService, property } from 'src/core/services/data.service';
import { BookemZulu as Model } from 'src/core/services/data/JayDataContext';
import { ContextService } from 'src/core/services/context.service';
import { PolicyCheckingService } from 'src/app/bookings/shared/policy-checking.service';
import { DriverQuoteEntryValidator } from 'src/core/customValidator/driver-quote-entry-validator';
import { ObjectUtils } from 'src/core/utils/object';
import { StatusTexts } from "src/app/shared/status-texts";

export class QuoteEntryDataService extends DataService<typeof Model.Bookings.Logic.ViewModels.QuoteEntryItem, Model.Bookings.Logic.ViewModels.QuoteEntryItem, QuoteEntryItem> {
	constructor(private policyCheckingService: PolicyCheckingService, context: ContextService, saveAsDelete: boolean = false) {
		super(context, (ctx) => ctx.QuoteEntries, saveAsDelete);
	}

	protected map(entity: Model.Bookings.Logic.ViewModels.QuoteEntryItem): QuoteEntryItem {
		return new QuoteEntryItemModel(entity);
	}

	static make(policyCheckingService: PolicyCheckingService, context?: ContextService, saveAsDelete: boolean = false): QuoteEntryDataService {
		return new QuoteEntryDataService(policyCheckingService, context, saveAsDelete);
	}

	async addProductManually(quote: QuoteItem, resultSelected: ProductManually): Promise<QuoteEntryItem> {
		const entity = new Model.Bookings.Logic.ViewModels.QuoteEntryItem();
		entity.id = uuid.v4();
		//TODO se autoconectan los productos manuales? 
		entity.productType = resultSelected.productType;
		entity.productDisplayName = resultSelected.productDisplayName;
		entity.dateFrom = resultSelected.dateFrom;
		entity.dateTo = resultSelected.dateTo;
		entity.isVisible = true;
		entity.supplierId = resultSelected.supplierId;
		entity.supplierTitle = resultSelected.supplierTitle;
		entity.costPrice = resultSelected.costPrice.toString();
		entity.sellPrice = resultSelected.sellPrice.toString();
		entity.salesmanProfit = (parseFloat(entity.sellPrice.toString()) - parseFloat(entity.costPrice.toString())).toString();
		entity.salePriceGross = entity.sellPrice;
		entity.invoicedCost = '0';
		entity.paidCost = '0';
		let json: { [prop: string]: any } = {};
		if (entity.productType == 1 || entity.productType == 5) {
			const accommodation = resultSelected as AccommodationProductManually;
			json.rating = accommodation.rating;
			json.code = accommodation.productServiceType.displayName;
			json.categoryId = accommodation.productCategory.id;
			//json.breakfast = accommodation.productServiceType.;
			//json.lunch = accommodation.productServiceType.lunch;
			//json.dinner = accommodation.productServiceType.dinner;
			//json.oneMeal = accommodation.productServiceType.oneMeal;
			//json.allHotelServices = accommodation.productServiceType.allHotelServices;
		}
		if (entity.productType == 2) {
			const carRent = resultSelected as CarRentProductManually;
			json.driver = carRent.driver;
			json.officePickupId = carRent.officePickupId;
			json.officeDropOffId = carRent.officeDropOffId;
			json.code = carRent.productServiceType.displayName;
			json.categoryId = carRent.productCategory.id;
			//json.manual = carRent.features.manual;
			//json.automatic = carRent.features.automatic;
		}
		if (entity.productType == 3) {
			const transfer = resultSelected as TransferProductManually;
			json.duration = transfer.duration;
			json.originPlaceId = transfer.originPlaceId;
			json.destinationPlaceId = transfer.destinationPlaceId;
			json.code = transfer.productServiceType.displayName;
			json.categoryId = transfer.productCategory.id;
			//json.collectiveTransfer = transfer.features.collectiveTransfer;
			//json.privateTransfer = transfer.features.privateTransfer;
			//json.railwayTransfer = transfer.features.railwayTransfer;
		}
		if (entity.productType == 4) {
			const flight = resultSelected as FlightProductManually;
			json.categoryId = flight.productCategory.id;
		}
		if (entity.productType == 0) {
			const general = resultSelected as GeneralProductManually;
			json.categoryId = general.productCategory.id;
		}
		if (entity.productType == 7) {
			const sales = resultSelected as SalesAdjustmentProductManually;
			if (sales.quoteEntryId) {
				entity.parentEntryId = sales.quoteEntryId;
			}
			if (sales.salesmanProfit) {
				entity.salesmanProfit = sales.salesmanProfit.toString();
			}
		}

		json.productProviderApiName = 'ProductBookingSearchService';
		json.costPriceResult = !!resultSelected.costPriceResult ? resultSelected.costPriceResult : { totalPrice: 0, exchangeRate: 0 };
		json.sellPriceResult = !!resultSelected.sellPriceResult ? resultSelected.sellPriceResult : { totalPrice: 0, exchangeRate: 0 };
		json.taxId = quote.defaultTaxId;
		json.history = [];
		json.notes = [];
		json.erratasConfirmed = [];
		let occupancy: QuoteEntryOccupancy[] = [];
		for (const oc of resultSelected.occupancies) {
			const type = oc.age > 12
				? 0
				: oc.age > 2
					? 1
					: 2;
			occupancy.push({ id: oc.id, type });
		}
		json.occupancy = occupancy;
		json.state = 'consistent';
		json.requiresConfirmation = resultSelected.requiresConfirmation;
		json.visibility = { client: [], agent: [], supplier: [] };
		json.calculatedCostPrice = parseFloat(entity.costPrice);
		json.calculatedSalePriceGross = parseFloat(entity.salePriceGross);
		(entity as any).dynamicProperties = json;
		if (quote.status === 0) {
			entity.status = 0;
		} else {
			if (resultSelected.requiresConfirmation == true) {
				entity.status = 1;
			} else {
				entity.status = 3;
			}
		}

		let item = await this.create(() => entity);
		item.entity.quote = quote.entity;
		return item;
	}

	async add(
		quote: QuoteItem,
		products: ResultSelection[],
		search: Search,
		parentEntryId?: string,
		taxId?: number
	): Promise<QuoteEntryItem[]> {
		const results: QuoteEntryItem[] = [];
		// TODO for not live prices, check real prices in server
		for (const selection of products) {
			const entity = new Model.Bookings.Logic.ViewModels.QuoteEntryItem();
			entity.id = uuid.v4();

			const resultSelected = selection.selected.result;
			if (parentEntryId != null) {
				entity.parentEntryId = parentEntryId;
			}
			if (quote.id > 0) {
				entity.quoteId = quote.id;
			}

			entity.productType = resultSelected.productType;
			entity.productId = resultSelected.product.internalId;
			entity.productDisplayName = resultSelected.product.name;
			entity.dateFrom = resultSelected.startDate;
			entity.dateTo = resultSelected.endDate;
			entity.isVisible = true;
			entity.supplierId = resultSelected.supplier.internalId;
			entity.supplierTitle = resultSelected.supplier.title;

			const selectedOptions = selection.supDisc.filter(e => !(e.result as SupplementSearchResult).isRequired);
			const totalCostOptions = selectedOptions.reduce((prev, curr) => prev + curr.cost.totalPrice, 0);
			const totalSellOptions = selectedOptions.reduce((prev, curr) => prev + curr.sell.totalPrice, 0);
			entity.costPrice = !!selection.selected.cost ? ((selection.selected.cost.totalPrice || 0) + totalCostOptions).toString() : '0';
			entity.sellPrice = !!selection.selected.sell ? ((selection.selected.sell.totalPrice || 0) + totalSellOptions).toString() : '0';
			const salesmanProfitRaw = parseFloat(entity.sellPrice) - parseFloat(entity.costPrice) * (!!selection.selected.sell ? selection.selected.sell.exchangeRate || 1 : 1);
			entity.salesmanProfit = ObjectUtils.round(salesmanProfitRaw).toString();
			entity.salePriceGross = entity.sellPrice || '0'; // TODO: fill this field
			entity.invoicedCost = '0';
			entity.paidCost = '0';

			let json: any = {};
			const extraProperties = JSON.parse(resultSelected.json);
			if (extraProperties != null) {
				json.extraProperties = extraProperties;
			}
			const parent = selection.selected.parent;
			if (parent) {
				if (parent.product.internalId == null || parent.product.internalId !== resultSelected.product.internalId) {
					json.parentInternalId = parent.product.internalId;
					json.parentExternalId = parent.product.externalId;
					json.parentName = parent.product.name;
					entity.productDisplayName = parent.product.name + " " + entity.productDisplayName;

				}

			}

			if (entity.productType == 1 || entity.productType == 5) {
				const accommodation = resultSelected as AccommodationSearchResult;
				json.rating = accommodation.rating;

				if (!!accommodation.features) {
					json.code = accommodation.features.code;
					json.categoryId = accommodation.features.internalId;
					json.breakfast = accommodation.features.breakfast;
					json.lunch = accommodation.features.lunch;
					json.dinner = accommodation.features.dinner;
					json.oneMeal = accommodation.features.oneMeal;
					json.allHotelServices = accommodation.features.allHotelServices;
				}
				json.placeId = parent && parent.productType !== 9 ? (parent as AccommodationSearchResult).place.internalId : accommodation.place.internalId

			}
			if (entity.productType == 2) {
				const carRent = resultSelected as CarRentSearchResult;
				json.driver = null;
				json.officePickupId = carRent.pickupPlace ? carRent.pickupPlace.internalId : undefined;
				json.officeDropOffId = carRent.dropoffPlace ? carRent.dropoffPlace.internalId : undefined;
				json.code = carRent.features.code;
				json.manual = carRent.features.manual;
				json.automatic = carRent.features.automatic;
				json.categoryId = carRent.features.internalId;
			}
			if (entity.productType == 3) {
				const transfer = resultSelected as TransferSearchResult;
				json.duration = transfer.duration;
				json.originPlaceId = transfer.pickupPlace.internalId;
				json.destinationPlaceId = transfer.dropoffPlace.internalId;
				json.code = transfer.features.code;
				json.collectiveTransfer = transfer.features.collectiveTransfer;
				json.privateTransfer = transfer.features.privateTransfer;
				json.railwayTransfer = transfer.features.railwayTransfer;
				json.categoryId = transfer.features.internalId;
			}
			if (entity.productType == 4) {
				const flight = resultSelected as FlightSearchResult;
				json.flightLegs = flight.legs;
				//TODO que leg se escoge para hacer el autoconect
				//json.placeId = flight.legs.length > 0 ? flight.legs[0].destinationAirport.internalId:undefined;
				//json.categoryId = flight.features.categoryId;
			}
			if (entity.productType == 0) {
				const general = resultSelected as GeneralSearchResult;
				json.categoryId = general.features.internalId;
				json.placeId = general.place.internalId;
			}
			if (entity.productType == 9) { // Tour
				const tour = resultSelected as TourSearchResult;
				json.isPackage = tour.features.isPackage;
				if (!json.isPackage) {
					const price = tour.subProducts.reduce((prev, curr) => {
						let props = Object.getOwnPropertyNames(toJS(curr.sellPrice));
						if (props.length === 0) {
							curr = curr.subProducts[0];
							props = Object.getOwnPropertyNames(toJS(curr.sellPrice));
						}
						const newPrice = props.reduce((p, c) => ({ cost: p.cost + curr.costPrice[c].totalPrice, sell: p.sell + curr.sellPrice[c].totalPrice }), ({ cost: 0, sell: 0 }));
						return { cost: prev.cost + newPrice.cost, sell: prev.sell + newPrice.sell };
					}, { cost: 0, sell: 0 });
					entity.costPrice = price.cost.toString();
					entity.sellPrice = price.sell.toString();
					const salesmanProfitRaw = parseFloat(entity.sellPrice) - parseFloat(entity.costPrice) * (!!selection.selected.sell ? selection.selected.sell.exchangeRate || 1 : 1);
					entity.salesmanProfit = ObjectUtils.round(salesmanProfitRaw).toString();
					entity.salePriceGross = entity.sellPrice || '0'; // TODO: fill this field
				}
				if (tour.features.tourItineraryDateId) {
					json.tourItineraryDateId = tour.features.tourItineraryDateId;
				}
			} else {
				if (parent && parent.productType === 9 && (parent as TourSearchResult).features.isPackage) {
					json.isPackage = true;
				}
			}
			json.productProviderApiName = resultSelected.productProviderApiName;
			json.costPriceResult = selection.selected.cost;
			json.sellPriceResult = selection.selected.sell;
			json.calculatedCostPrice = parseFloat(entity.costPrice);
			json.calculatedSalePriceGross = parseFloat(entity.salePriceGross);
			json.taxId = taxId || quote.defaultTaxId;
			json.history = [];
			json.notes = [];
			json.erratasConfirmed = [];
			const occupancy: QuoteEntryOccupancy[] = [];
			for (const paxId of selection.selected.cost.paxs) {
				occupancy.push({ id: paxId, type: resultSelected.occupancy[paxId] });
			}
			json.occupancy = occupancy;
			json.state = 'consistent';
			json.requiresConfirmation = resultSelected.requiresConfirmation;
			json.allotmentAvailable = resultSelected.allotmentAvailable;
			json.allotmentId = resultSelected.allotmentId;
			(entity as any).dynamicProperties = json;
			if (quote.status === 0) {
				entity.status = 0;
			} else {
				if (resultSelected.requiresConfirmation == true) {
					entity.status = 1;
				} else {
					entity.status = 3;
				}
			}

			const result = await this.create(() => entity)
				.then((item) => {
					item.entity.quote = quote.entity;
					this.sortItem(item);
					return item;
				})
				.then((item) => this.computeDependencies(quote, item, search as any))
			results.push(result);

			if (entity.productType == 9) { // Tour
				const tour = resultSelected as TourSearchResult;
				for (const sub of tour.subProducts.filter(i => i.productType !== 6)) {
					this.add(quote, this.convertTourSubProductToSelection(sub, tour), search, entity.id, taxId);
				}
			}

			const subResults: Promise<QuoteEntryItem[]>[] = [];
			for (const sub of selection.supDisc) {
				if (sub.cost && sub.sell) {
					subResults.push(this.add(quote, [{ selected: sub, supDisc: [] }], search, entity.id, taxId));
				}
			}

			const subs = await Promise.all(subResults);
			for (const sub of subs) {
				for (const i of sub) {
					results.push(i);
				}
			}
			for (const entry of results) {
				let visibility = await this.policyCheckingService.getVisibilityProduct(quote, entry, search as any);
				entry.dynamicProperties.visibility = visibility || { client: [], agent: [], supplier: [] };
			}
		}

		return results;
	}

	async cloneItem(base: QuoteEntryItem): Promise<QuoteEntryItem> {
		const entity = new Model.Bookings.Logic.ViewModels.QuoteEntryItem();
		entity.id = uuid.v4();

		entity.quoteId = base.quoteId;
		entity.parentEntryId = base.parentEntryId;
		entity.productType = base.productType;
		entity.productDisplayName = base.productDisplayName;
		entity.dateFrom = base.dateFrom;
		entity.dateTo = base.dateTo;
		entity.isVisible = base.isVisible;
		entity.supplierId = base.supplierId;
		entity.supplierTitle = base.supplierTitle;
		entity.costPrice = base.costPrice.toString();
		entity.sellPrice = base.sellPrice.toString();
		entity.salesmanProfit = base.salesmanProfit.toString();
		entity.salePriceGross = base.salePriceGross.toString();
		entity.invoicedCost = base.invoicedCost.toString();
		entity.paidCost = base.paidCost.toString();
		(entity as any).dynamicProperties = { ...base.dynamicProperties };
		entity.status = base.status;

		let item = await this.create(() => entity);
		item.entity.quote = base.quote.entity;

		return item;
	}

	async adjustEntryPrice(quote: QuoteItem, quoteEntries: QuoteEntryItem[], saleAdjustment: SalesAdjustmentProductManually, updateQuote: (quoteEntries: QuoteEntryItem[]) => void): Promise<void> {
		const parentQuoteEntry = quoteEntries.find(m => m.id === saleAdjustment.quoteEntryId);
		let saleAdjustmentEntry = quoteEntries.find(m => m.parentEntryId === parentQuoteEntry.id && m.productType == 7);
		if (saleAdjustmentEntry == null) {
			saleAdjustmentEntry = await this.addProductManually(quote, saleAdjustment);
			saleAdjustmentEntry.dynamicProperties.occupancy = parentQuoteEntry.dynamicProperties.occupancy;
			quoteEntries.push(saleAdjustmentEntry);
		} else {
			saleAdjustmentEntry.costPrice = saleAdjustment.costPrice;
			saleAdjustmentEntry.sellPrice = saleAdjustment.sellPrice;
			saleAdjustmentEntry.salePriceGross = saleAdjustment.salePriceGross;
			saleAdjustmentEntry.salesmanProfit = saleAdjustment.salesmanProfit;
		}

		parentQuoteEntry.dynamicProperties.hasSaleAdjustment = true;
		parentQuoteEntry.dynamicProperties.adjustmentType = saleAdjustment.isRelative ? 'relative' : 'absolute';
		parentQuoteEntry.salesmanProfit = saleAdjustment.salesmanProfit;
		if (saleAdjustment.isRelative) {
			parentQuoteEntry.costPrice = parentQuoteEntry.dynamicProperties.calculatedCostPrice + saleAdjustment.costPrice;
		} else {
			parentQuoteEntry.costPrice = saleAdjustment.costPrice;
			parentQuoteEntry.salePriceGross = saleAdjustment.salePriceGross;
		}

		updateQuote(quoteEntries);
	}

	async computeDependencies(quote: QuoteItem, item: QuoteEntryItem, search: Search & SearchDates): Promise<QuoteEntryItem> {
		const searchResults = await this.policyCheckingService.getAdditionalProduct(quote, item, search);
		const items = searchResults
			.map(e => e.items)
			.reduce((prev, curr) => [...prev, ...curr], []);
		const promises = items.map(async (searchResult) => {
			const selection = this.convertResultToSelection(searchResult);
			const [entry] = await this.add(quote, selection, search, item.id, item.dynamicProperties.taxId);
			entry.dynamicProperties.isProductFee = true;
		});

		await Promise.all(promises);
		return item;
	}

	public convertTourSubProductToSelection(searchResult: ProductSearchResult, parent: TourSearchResult) {
		const selection: ResultSelection[] = [];
		let result = searchResult;
		let props = Object.getOwnPropertyNames(toJS(result.sellPrice));
		if (props.length === 0 && result.subProducts.length === 1) {
			result = result.subProducts[0];
			result.product.name = searchResult.product.name + " " + result.product.name;
			if (result.productType === 5) {
				(result as AccommodationSearchResult).place = (searchResult as AccommodationSearchResult).place;
			}
			props = Object.getOwnPropertyNames(toJS(result.sellPrice));
		}
		if (props.length === 0) {
			selection.push({
				selected: {
					id: result.id,
					unitIndex: 1,
					result: result,
					parent,
					cost: {
						currencyIsoCode: parent.costPrice[1].currencyIsoCode,
						exchangeRate: 1,
						isPriceLive: true,
						isCommissionable: false,
						sourceCurrencyIsoCode: parent.costPrice[1].currencyIsoCode,
						totalPrice: 0,
						breakdown: null,
						paxs: parent.costPrice[1].paxs,
					},
					sell: {
						currencyIsoCode: parent.sellPrice[1].currencyIsoCode,
						exchangeRate: 1,
						isPriceLive: false,
						isCommissionable: false,
						sourceCurrencyIsoCode: parent.sellPrice[1].currencyIsoCode,
						totalPrice: 0,
						breakdown: null,
						paxs: parent.sellPrice[1].paxs,
					}
				},
				supDisc: result.subProducts.map(e => ({
					id: e.id,
					unitIndex: 1,
					cost: {
						currencyIsoCode: parent.costPrice[1].currencyIsoCode,
						exchangeRate: 1,
						isPriceLive: true,
						isCommissionable: false,
						sourceCurrencyIsoCode: parent.costPrice[1].currencyIsoCode,
						totalPrice: 0,
						breakdown: null,
						paxs: parent.costPrice[1].paxs,
					},
					sell: {
						currencyIsoCode: parent.sellPrice[1].currencyIsoCode,
						exchangeRate: 1,
						isPriceLive: false,
						isCommissionable: false,
						sourceCurrencyIsoCode: parent.sellPrice[1].currencyIsoCode,
						totalPrice: 0,
						breakdown: null,
						paxs: parent.sellPrice[1].paxs,
					},
					result: e,
					parent: result,
				})),
			});
		} else {
			for (const prop of props) {
				const unitIndex = parseInt(prop);
				const cost = result.costPrice[unitIndex];
				const sell = result.sellPrice[unitIndex];
				selection.push({
					selected: {
						id: result.id,
						unitIndex: unitIndex,
						result: result,
						parent,
						cost: cost,
						sell: sell
					},
					supDisc: result.subProducts.map(e => ({
						id: e.id,
						unitIndex: unitIndex,
						cost: e.costPrice[unitIndex],
						sell: e.sellPrice[unitIndex],
						result: e,
						parent: result,
					})),
				});
			}
		}
		return selection;
	}

	public convertResultToSelection(searchResult: ProductSearchResult) {
		const selection: ResultSelection[] = [];
		for (const prop of Object.getOwnPropertyNames(searchResult.costPrice)) {
			const unitIndex = parseInt(prop);
			const cost = searchResult.costPrice[unitIndex];
			const sell = searchResult.sellPrice[unitIndex];
			if (cost && sell) {
				selection.push({
					selected: {
						id: searchResult.id,
						unitIndex: unitIndex,
						cost: cost,
						sell: sell,
						result: searchResult,
					},
					supDisc: searchResult.subProducts
						.filter((e: SupplementSearchResult) => e.isRequired)
						.map(e => {
							const cost = e.costPrice[unitIndex];
							const sell = e.sellPrice[unitIndex];
							return {
								id: e.id,
								unitIndex: unitIndex,
								cost: cost,
								sell: sell,
								result: e,
							};
						})
						.filter((e) => e.cost && e.sell),
				});
			}
		}
		return selection;
	}

	private findDependents(entry: QuoteEntryItem, fullyEvaluated: Map<string, boolean>, dependents: QuoteEntryItem[], fullEvaluation: boolean = false): void {
		if (fullyEvaluated.has(entry.id) && (fullyEvaluated.get(entry.id) || !fullEvaluation)) {
			return;
		}
		fullyEvaluated.set(entry.id, fullEvaluation);

		if (fullEvaluation) {
			dependents.push(entry);
		}
		const children: QuoteEntryItem[] = this.items
			.filter(m => m.parentEntryId === entry.id);
		for (let curr of children) {
			if (curr.dynamicProperties.isProductFee || fullEvaluation) {
				this.findDependents(curr, fullyEvaluated, dependents, true);
			}
		}
	}

	deleteDescendantItems(items: QuoteEntryItem[], includeRoots: boolean = true): Promise<number> {
		const roots = new Map<string, boolean>();
		const dependents: QuoteEntryItem[] = [];
		for (let i = 0; i < items.length; i++) {
			this.findDependents(items[i], roots, dependents, includeRoots);
		}

		return super.delete(dependents);
	}

	delete(items: QuoteEntryItem[]): Promise<number> {
		return this.deleteDescendantItems(items);
	}

	public updateOrder(item: QuoteEntryItem, newOrder: number) {
		const oldOrder = item.order;
		let startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
		const toUpdates = this.items.filter(e => e.parentEntryId == null);
		while (startIndex <= Math.max(newOrder, oldOrder)) {
			if (toUpdates.length < newOrder) {
				newOrder = toUpdates.length;
				startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
			}
			if (newOrder < 1) {
				newOrder = 1;
				startIndex = newOrder < oldOrder ? newOrder : oldOrder + 1;
			}

			const toUpdate = toUpdates.find(e => startIndex == e.order);
			if (toUpdate == null) {
				break;
			}

			const order = newOrder < oldOrder ? startIndex++ + 1 : startIndex++ - 1;
			toUpdate.order = order;
			let children = this.items.filter(e => e.parentEntryId == toUpdate.id);
			children.forEach((e) => e.order = toUpdate.order);
			if (startIndex == oldOrder) {
				break;
			}
		}
		item.order = newOrder;
		let children = this.items.filter(e => e.parentEntryId == item.id);
		children.forEach((e) => e.order = item.order);
		this.items = this.items.sort((x, y) => x.order - y.order);
	}

	public updateOrders() {
		this.items = this.items.sort((x, y) => {
			return moment.utc(x.dateFrom).isBefore(y.dateFrom)
				? -1
				: moment.utc(x.dateFrom).isAfter(y.dateFrom)
					? 1
					: moment.utc(x.dateTo).isBefore(y.dateTo)
						? -1
						: moment.utc(x.dateTo).isAfter(y.dateTo)
							? 1
							: (x.order - y.order);
		});
		let count = 1;
		let firstLevel = this.items.filter(e => e.parentEntryId == null);
		for (const i of firstLevel) {
			i.order = count++;
			let children = this.items.filter(e => e.parentEntryId == i.id);
			children.forEach((e) => e.order = i.order);
		}
	}

	private sortItem(item: QuoteEntryItem) {
		//console.log(item.order, item.productDisplayName, item.dateFrom, item.productType, this.items.length);
		if (item.parentEntryId != null) {
			let parent = this.items.find(e => e.id == item.parentEntryId);
			if (parent) {
				item.order = parent.order;
			}
		} else {
			let firstLevel = this.items.filter(e => e.parentEntryId == null);
			if (firstLevel.length === 1) {
				item.order = 1;
				return;
			}
			let tmp = item;
			const dateFrom = moment.utc(item.dateFrom);
			for (const i of firstLevel) {
				if (i.order === undefined || i.order == null) {
					i.order = firstLevel.length;
				}
				if (dateFrom.isBefore(i.dateFrom)) {
					tmp.order = i.order;
					i.order = null;
					tmp = i;
				}
			}
			if (!tmp.order) {
				tmp.order = firstLevel.length;
			}
		}
		this.items = this.items.slice().sort((x, y) => x.order - y.order);
		//console.log(item.order, item.productDisplayName, item.dateFrom, item.productType);
	}
}

export class QuoteEntryItemModel extends EntityModel<Model.Bookings.Logic.ViewModels.QuoteEntryItem, QuoteEntryItem> implements QuoteEntryItem {
	@property<QuoteEntryItemModel>(o => o.entity.id, (o, value) => o.entity.id = value)
	public id: string;

	@property<QuoteEntryItemModel>(o => o.entity.order, (o, value) => o.entity.order = value)
	public order: number;

	@property<QuoteEntryItemModel>(o => o.entity.dateFrom, (o, value) => o.entity.dateFrom = value)
	public dateFrom: Date;

	@property<QuoteEntryItemModel>(o => o.entity.dateTo, (o, value) => o.entity.dateTo = value)
	public dateTo: Date;

	@property<QuoteEntryItemModel>(o => o.entity.isVisible, (o, value) => o.entity.isVisible = value)
	public isVisible: boolean;

	@property<QuoteEntryItemModel>(o => parseFloat(o.entity.costPrice.toString()), (o, value) => o.entity.costPrice = ObjectUtils.round(value).toString())
	public costPrice: number;

	@property<QuoteEntryItemModel>(o => parseFloat(o.entity.sellPrice.toString()), (o, value) => o.entity.sellPrice = ObjectUtils.round(value).toString())
	public sellPrice: number;

	@property<QuoteEntryItemModel>(o => parseFloat(o.entity.salesmanProfit.toString()), (o, value) => o.entity.salesmanProfit = ObjectUtils.round(value).toString())
	public salesmanProfit: number;

	@property<QuoteEntryItemModel>(o => parseFloat(o.entity.salePriceGross.toString()), (o, value) => o.entity.salePriceGross = ObjectUtils.round(value).toString())
	public salePriceGross: number;

	@property<QuoteEntryItemModel>(o => parseFloat(o.entity.invoicedCost.toString()), (o, value) => o.entity.invoicedCost = ObjectUtils.round(value).toString())
	public invoicedCost: number;

	@property<QuoteEntryItemModel>(o => parseFloat(o.entity.paidCost.toString()), (o, value) => o.entity.paidCost = ObjectUtils.round(value).toString())
	public paidCost: number;

	@property<QuoteEntryItemModel>(o => o.entity.confirmationReferenceNumber, (o, value) => o.entity.confirmationReferenceNumber = value)
	public confirmationReferenceNumber: string;

	@property<QuoteEntryItemModel>(o => o.entity.status, (o, value) => o.entity.status = value)
	public status: number;

	@property<QuoteEntryItemModel>(o => o.entity.productType, (o, value) => o.entity.productType = value)
	public productType: number;

	@computed
	get productTypeText(): 'Accommodation' | 'CarRent' | 'General' | 'Transfer' | 'Flights' | 'Tour' {
		switch (this.productType) {
			case 0:
				return 'General';
			case 1:
			case 5:
				return 'Accommodation';
			case 2:
				return 'CarRent';
			case 3:
				return 'Transfer';
			case 4:
				return 'Flights';
			case 9:
				return 'Tour';
		}

		return 'General';
	}

	@computed
	public get statusText(): string {
		return StatusTexts.getQuoteEntryStatusFromNumber(this.entity.status)
	}

	@property<QuoteEntryItemModel>(o => o.entity.quoteId, (o, value) => o.entity.quoteId = value)
	//@IsDefined({ message: 'quote is required' })
	public quoteId: number;

	@property<QuoteEntryItemModel>(o => o.entity.quote, (o, value) => o.entity.quote = value)
	public quote: any;

	@property<QuoteEntryItemModel>(o => o.entity.productId, (o, value) => o.entity.productId = value)
	public productId?: number;

	@property<QuoteEntryItemModel>(o => o.entity.externalId, (o, value) => o.entity.externalId = value)
	public externalId: string;

	@property<QuoteEntryItemModel>(o => o.entity.productDisplayName, (o, value) => o.entity.productDisplayName = value)
	public productDisplayName: string;

	@property<QuoteEntryItemModel>(o => o.entity.supplierId, (o, value) => o.entity.supplierId = value)
	public supplierId: number;

	@property<QuoteEntryItemModel>(o => o.entity.supplierTitle, (o, value) => o.entity.supplierTitle = value)
	public supplierTitle: string;

	@property<QuoteEntryItemModel>(o => o.entity.parentEntryId, (o, value) => o.entity.parentEntryId = value)
	public parentEntryId: string;

	@property<any>(o => o.entity.dynamicProperties, (o, value) => o.entity.dynamicProperties = value)
	@Validate(DriverQuoteEntryValidator)
	public dynamicProperties: {};

	@property<QuoteEntryItemModel>(o => o.entity.allotmentRequestId, (o, value) => o.entity.allotmentRequestId = value)
	public allotmentRequestId?: number;
}



// WEBPACK FOOTER //
// ./src/app/bookings/quote-entries/quote-entry-form.data.ts